import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'

type EventType = MouseEvent | TouchEvent
const DEFAULT_EVENTS = ['mousedown', 'touchstart']

export function useClickOutside<T extends HTMLElement = HTMLElement>(
  callback: (event: EventType) => void,
  events?: string[] | null,
  nodes?: (HTMLElement | null)[],
): RefObject<T> {
  const ref = useRef<T>(null)
  const eventsList = events || DEFAULT_EVENTS

  useEffect(() => {
    const listener = (event: Event): void => {
      const { target } = event ?? {}
      if (Array.isArray(nodes)) {
        const shouldIgnore =
          !document.body.contains(target as Node) && (target as Element)?.tagName !== 'HTML'
        const shouldTrigger = nodes.every(node => !!node && !event.composedPath().includes(node))
        if (shouldTrigger && !shouldIgnore) {
          callback(event as EventType)
        }
      } else if (ref.current && !ref.current.contains(target as Node)) {
        callback(event as EventType)
      }
    }

    eventsList.forEach(fn => document.addEventListener(fn, listener))

    return (): void => {
      eventsList.forEach(fn => document.removeEventListener(fn, listener))
    }
  }, [ref, callback, nodes])

  return ref
}
